import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import type { PublicStoreConfig } from '../types';

interface State {
  config: PublicStoreConfig | null;
  loading: boolean;
  error: string | null;
}

/** Fetch the public-facing store configuration for a slug. */
export function useStoreConfig(slug: string): State {
  const [state, setState] = useState<State>({
    config: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ config: null, loading: true, error: null });

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/stores/${encodeURIComponent(slug)}/public`);
        if (!res.ok) {
          throw new Error(`Store not found (${res.status})`);
        }
        const json = await res.json();
        // Endpoint may return the config directly or wrapped in { data }.
        const config: PublicStoreConfig = json?.data ?? json;
        if (!cancelled) {
          setState({ config, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            config: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load store',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return state;
}
